// useMealPlanSave.js — Save, login, and replace meal plan logic
import { useState, useRef, useEffect, useCallback } from "react";

import backendClient from "../../../services/backendClient";
import { clearInProgressMealPlan } from "../../../utils/inProgressMealPlan";
import { trackSaveClicked, trackMealSetSaved, CONTEXT_ACTION } from "../../../analytics";

import { countAllProducts } from "../utils/mealPlanHelpers";

/**
 * Manages saving meal plans to backend, login flow, and replace confirmation.
 *
 * @param {Object} deps
 * @param {Array} deps.mealPlan
 * @param {Function} deps.setMealPlan
 * @param {Array} deps.productList
 * @param {Function} deps.setProductList
 * @param {string} deps.totalCost
 * @param {Function} deps.setTotalCost
 * @param {string|null} deps.adjustedTotalCost
 * @param {Function} deps.setAdjustedTotalCost
 * @param {Object} deps.haveItState
 * @param {Function} deps.setHaveItState
 * @param {Array} deps.leftovers
 * @param {Function} deps.setLeftovers
 * @param {Object} deps.cookingStyle
 * @param {boolean} deps.isFromWizard
 * @param {React.MutableRefObject<boolean>} deps.isFromWizardRef
 * @param {boolean} deps.restoredFromLocalStorage
 * @param {Function} deps.setWasLoadedFromDb
 * @param {Function} deps.setIsLoginModalOpen
 * @param {Function} deps.setReplaceMealPlanDrawerOpen
 * @param {Function} deps.onSaveMealPlan
 */
export function useMealPlanSave({
  mealPlan,
  setMealPlan,
  productList,
  setProductList,
  totalCost,
  setTotalCost,
  adjustedTotalCost,
  setAdjustedTotalCost,
  haveItState,
  setHaveItState,
  leftovers,
  setLeftovers,
  myProducts,
  setMyProducts,
  cookingStyle,
  isFromWizard,
  isFromWizardRef,
  restoredFromLocalStorage,
  setWasLoadedFromDb,
  setIsLoginModalOpen,
  setReplaceMealPlanDrawerOpen,
  onSaveMealPlan,
}) {
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
  const [isSavingMealPlan, setIsSavingMealPlan] = useState(false);
  const [isReplacingMealPlan, setIsReplacingMealPlan] = useState(false);
  // TODO: successMessage and errorMessage are set but not displayed - consider adding toast notifications
  const [, setSuccessMessage] = useState("");
  const [, setErrorMessage] = useState("");
  const hasAttemptedAutoSave = useRef(false);
  const isHandlingLoginSuccess = useRef(false);
  // Holds the auth token received from OTP login while the Replace drawer is shown.
  // Re-applied in handleConfirmReplaceMealPlan so that any silent token-clearing
  // (e.g. a 401 from the prefs-sync PUT that backendClient handles by removing the
  // token) cannot break the subsequent save.
  const pendingLoginTokenRef = useRef(null);

  /**
   * Apply backend meal plan response to state
   */
  const applyBackendData = useCallback(
    (data) => {
      setMealPlan(data.mealPlan);
      if (data.productList) setProductList(data.productList);
      if (data.totalCost) setTotalCost(data.totalCost);
      if (data.leftovers) setLeftovers(data.leftovers);
      if (data.haveItState) setHaveItState(data.haveItState);
      if (data.myProducts && setMyProducts) setMyProducts(data.myProducts);
      setWasLoadedFromDb(true);
    },
    [setMealPlan, setProductList, setTotalCost, setLeftovers, setHaveItState, setMyProducts, setWasLoadedFromDb]
  );

  /**
   * Save current meal plan to backend
   */
  const handleSaveMealPlan = useCallback(async () => {
    trackSaveClicked({ context_action: CONTEXT_ACTION.SAVE });
    setIsSavingMealPlan(true);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        setIsLoginModalOpen(true);
        setIsSavingMealPlan(false);
        return;
      }

      const mealPlanData = {
        mealPlan,
        cookingStyle: cookingStyle || {
          breakfast: { cookingTime: 'standard', cookFor2Days: false },
          lunch: { cookingTime: 'standard', cookFor2Days: false },
          dinner: { cookingTime: 'standard', cookFor2Days: false },
        },
        groceryList: {
          products: productList,
          totalCost,
          adjustedTotalCost,
          haveItState: haveItState || {},
          leftovers: leftovers || [],
          myProducts: myProducts || [],
        },
      };

      console.log("💾 Saving meal plan - checking cookingTime in recipes:");
      mealPlan.forEach((recipe, idx) => {
        console.log(`  [${idx}] ${recipe.title}: cookingTime = "${recipe.cookingTime}"`);
      });

      await backendClient.post("/api/mealplan/save", mealPlanData, { timeout: 30000 });

      // Re-fetch after save
      const data = await backendClient.get("/api/mealplan");
      console.log("📦 Received meal plan from backend after save - checking cookingTime:");
      data.mealPlan?.forEach((recipe, idx) => {
        console.log(`  [${idx}] ${recipe.title}: cookingTime = "${recipe.cookingTime}"`);
      });

      applyBackendData(data);
      setSuccessMessage("Meal plan saved successfully!");

      // Analytics
      const haveItCount = Object.values(haveItState || {}).filter(Boolean).length;
      const productsCount = countAllProducts(productList);
      trackMealSetSaved({
        recipe_count: mealPlan?.length || 0,
        products_count: productsCount,
        have_it_count: haveItCount,
        total_cost: totalCost || 0,
        saved_from: 'meal_plan',
      });

      clearInProgressMealPlan();
      setIsSavingMealPlan(false);

      if (onSaveMealPlan) onSaveMealPlan();
    } catch (error) {
      console.error("Error saving meal plan:", error);
      if (error.isAuthError) {
        setIsLoginModalOpen(true);
      } else {
        setErrorMessage("Failed to save meal plan");
      }
      setIsSavingMealPlan(false);
    }
  }, [
    mealPlan, productList, totalCost, adjustedTotalCost, haveItState, leftovers,
    myProducts, cookingStyle, setIsLoginModalOpen, onSaveMealPlan, applyBackendData,
  ]);

  // Auto-save after auth on mount
  useEffect(() => {
    const autoSaveAfterAuth = async () => {
      if (isFromWizard || isFromWizardRef.current) {
        console.log("[useMealPlanSave] Skipping autoSave - coming from wizard");
        return;
      }
      if (restoredFromLocalStorage) {
        console.log("[useMealPlanSave] Skipping autoSave - restored from localStorage");
        return;
      }
      if (isHandlingLoginSuccess.current) {
        console.log("[useMealPlanSave] Skipping autoSave - handleLoginSuccess in progress");
        return;
      }
      if (hasAttemptedAutoSave.current) {
        console.log("[useMealPlanSave] Skipping autoSave - already attempted");
        return;
      }
      hasAttemptedAutoSave.current = true;

      if (authToken && mealPlan.length > 0) {
        await handleSaveMealPlan();
      }
    };
    autoSaveAfterAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, isFromWizard]);

  /**
   * Handle successful login — check for existing plan and save or show replace drawer
   */
  const handleLoginSuccess = useCallback(
    async (newToken) => {
      setIsLoginModalOpen(false);
      isHandlingLoginSuccess.current = true;

      try {
        localStorage.setItem("authToken", newToken);
        window.dispatchEvent(new Event('authTokenChanged'));
        setAuthToken(newToken);

        // Sync anonymous preferences (from localStorage) to the new backend account
        try {
          const stored = localStorage.getItem('9meals_preferences');
          if (stored) {
            const prefs = JSON.parse(stored);
            await backendClient.put('/api/user/preferences', prefs);
            console.log('[useMealPlanSave] Preferences synced to backend after login');
          }
        } catch (prefError) {
          console.warn('[useMealPlanSave] Could not sync preferences to backend:', prefError.message);
        }

        // Re-store the token: backendClient removes it from localStorage when any
        // request returns 401 (including the prefs PUT above), so re-apply it here
        // before making any further authenticated calls.
        localStorage.setItem("authToken", newToken);

        if (mealPlan.length > 0) {
          try {
            const existsData = await backendClient.get("/api/mealplan/exists");
            if (existsData && existsData.hasMealPlan) {
              console.log("[useMealPlanSave] User has existing meal plan, showing drawer");
              // Store the token so handleConfirmReplaceMealPlan can re-apply it
              // immediately before saving, guarding against any silent token-clearing
              // that may happen while the drawer is open (e.g. focus/visibility-driven
              // premium-status checks in useMealPlanLoader).
              pendingLoginTokenRef.current = newToken;
              setReplaceMealPlanDrawerOpen(true);
              return;
            }
          } catch (checkError) {
            console.log("[useMealPlanSave] Could not check for existing meal plan:", checkError);
          }

          isHandlingLoginSuccess.current = false;
          await handleSaveMealPlan();
          // Always navigate after save in the login flow — even if handleSaveMealPlan
          // already called onSaveMealPlan, a second navigate('/mymeals') is a harmless no-op.
          if (onSaveMealPlan) onSaveMealPlan();
          return;
        }

        isHandlingLoginSuccess.current = false;
        const data = await backendClient.get("/api/mealplan");
        if (data && data.mealPlan) {
          applyBackendData(data);
          setSuccessMessage("Meal plan loaded successfully!");
        }
      } catch (error) {
        console.error("Error in login success handler:", error);
        isHandlingLoginSuccess.current = false;
        if (error.isAuthError) {
          setIsLoginModalOpen(true);
        } else {
          setErrorMessage("Failed to load saved meal plan");
        }
      }
    },
    [mealPlan, setIsLoginModalOpen, setReplaceMealPlanDrawerOpen, handleSaveMealPlan, applyBackendData, onSaveMealPlan]
  );

  /**
   * Confirm replacing existing meal plan
   */
  const handleConfirmReplaceMealPlan = useCallback(async () => {
    setIsReplacingMealPlan(true);
    try {
      setReplaceMealPlanDrawerOpen(false);
      isHandlingLoginSuccess.current = false;
      // Re-apply the token obtained at login time. Any background request that
      // returned 401 between login-success and this click (e.g. the premium-status
      // check in useMealPlanLoader) would have silently removed it from localStorage
      // via backendClient, causing handleSaveMealPlan to think the user is logged out.
      if (pendingLoginTokenRef.current) {
        localStorage.setItem("authToken", pendingLoginTokenRef.current);
        pendingLoginTokenRef.current = null;
      }
      await handleSaveMealPlan();
    } catch (error) {
      console.error("Error replacing meal plan:", error);
      setErrorMessage("Failed to replace meal plan");
    } finally {
      setIsReplacingMealPlan(false);
    }
  }, [setReplaceMealPlanDrawerOpen, handleSaveMealPlan]);

  /**
   * Cancel replacing — load and display the existing saved meal plan (stay on page)
   */
  const handleCancelReplaceMealPlan = useCallback(async () => {
    setReplaceMealPlanDrawerOpen(false);
    isHandlingLoginSuccess.current = false;
    pendingLoginTokenRef.current = null;
    try {
      const data = await backendClient.get('/api/mealplan');
      if (data && data.mealPlan) {
        applyBackendData(data);
        clearInProgressMealPlan();
      }
    } catch (error) {
      console.error('[useMealPlanSave] Failed to load existing meal plan:', error);
    }
  }, [setReplaceMealPlanDrawerOpen, applyBackendData]);

  return {
    isSavingMealPlan,
    isReplacingMealPlan,
    handleSaveMealPlan,
    handleLoginSuccess,
    handleConfirmReplaceMealPlan,
    handleCancelReplaceMealPlan,
  };
}
